import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button } from '@7shifts/sous-chef-base';
import { useAuth } from './AuthContext';
import {
    initiateLogin,
    POST_LOGIN_REDIRECT_KEY,
    HARNESS_OPENER_ORIGIN_KEY
} from './authCodeFlow';
import { useTranslation } from '@src/i18n';
import appIcon from '../assets/7punches-app-icon.svg';
import './LoginPage.css';

/**
 * Minimal login screen. Clicking "Sign in" kicks off the OAuth redirect.
 * Keeps its own in-flight state so the button disables while the PKCE
 * material is being generated (async crypto) and the browser is redirecting.
 *
 * Also handles the webview postMessage path: if tokens arrive via
 * usePostMessageAuth while this page is mounted, redirect immediately.
 */
export function LoginPage() {
    const { isAuthenticated } = useAuth();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Webview / postMessage path: tokens landed while we were on this page.
    if (isAuthenticated) {
        const redirectTo =
            sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) ?? '/terminal';
        return <Navigate to={redirectTo} replace />;
    }

    async function handleLogin() {
        setError(null);
        setIsRedirecting(true);
        try {
            if (searchParams.get('harness') === '1') {
                const explicitOrigin = searchParams.get('origin');
                const openerOrigin =
                    explicitOrigin ??
                    (document.referrer
                        ? new URL(document.referrer).origin
                        : null);
                if (openerOrigin && isValidHttpOrigin(openerOrigin)) {
                    sessionStorage.setItem(
                        HARNESS_OPENER_ORIGIN_KEY,
                        openerOrigin
                    );
                }
            }

            // Note: no postLoginRedirect here — RequireAuth already stashed the
            // deep-link the user originally wanted. Overwriting with the /login
            // path would lose it. If sessionStorage is empty (user navigated
            // directly to /login), the callback defaults to "/".
            await initiateLogin();
            // initiateLogin triggers a browser navigation; no further work here.
        } catch (err) {
            setIsRedirecting(false);
            setError(
                err instanceof Error ? err.message : t('login.errorFallback')
            );
        }
    }

    return (
        <main className="login-page">
            <div className="login-page__container">
                <img
                    src={appIcon}
                    alt="7punches"
                    className="login-page__icon"
                />
                <div className="login-page__content">
                    <h1 className="login-page__title">{t('login.title')}</h1>
                    <p className="login-page__subtitle">
                        {t('login.subtitle')}
                    </p>
                </div>
                <Button
                    theme="primary"
                    size="full-width"
                    type="button"
                    onClick={handleLogin}
                    disabled={isRedirecting}
                >
                    {isRedirecting ? t('login.redirecting') : t('login.signIn')}
                </Button>
                {error && (
                    <p className="login-page__error" role="alert">
                        {error}
                    </p>
                )}
            </div>
        </main>
    );
}

function isValidHttpOrigin(value: string): boolean {
    try {
        const url = new URL(value);
        return (
            (url.protocol === 'http:' || url.protocol === 'https:') &&
            url.origin === value
        );
    } catch {
        return false;
    }
}
